/**
 * Which windows FusionWeb exchanges postMessage with (FUS-474, Security Backlog M1/M2).
 *
 * FusionWeb's listeners used to act on a message from any window — UPDATE-CONTEXT carries the session
 * JWT, so a page that framed FusionWeb could sign it in as anyone — and every outbound message went to
 * '*', MCP entitlements and message previews included. Now:
 *
 *   - Inbound: a host message is accepted only from an ANCESTOR window (FusionWeb's parent, any frame
 *     above it, or FusionWeb itself — ERA posts into the page it loads top-level), never from a frame
 *     FusionWeb embeds, and only from an allowed origin. Ancestors, not just the parent: the
 *     WingCommander extension puts each blade inside a srcdoc sidebar frame but posts from the top
 *     window.
 *   - Outbound: a message goes to the parent's (or top's) exact origin, and only when that origin is
 *     allowed. The origin comes from location.ancestorOrigins where the browser has it; otherwise
 *     (Firefox) from the last trusted message the parent or top window sent, which the browser stamps
 *     with its real origin; and only as a last resort from document.referrer, which after a navigation
 *     inside the frame (the dlrSecured sign-in redirect) names the previous page, not the parent.
 *
 * The allowed origins come from the server (FusionRuntimeSystemConfig.FusionEmbedAllowedOrigins) in
 * the fusion-embed-origins meta tag — the same list FusionWeb's frame-ancestors header uses. '*' in
 * the list means unrestricted (an EU trusted-apps deployment whose partner origins are not configured):
 * as before FUS-474, a message from any window is accepted, a popup's opener or a sibling frame included.
 * Matching follows CSP source rules; keep it in step with EmbedSecurityHeaders.IsAllowedOrigin (C#).
 */

export const ORIGINS_META_NAME = 'fusion-embed-origins';
export const ANY_ORIGIN = '*';

export function readAllowedOrigins(doc = document) {
  const meta = doc?.querySelector?.(`meta[name="${ORIGINS_META_NAME}"]`);
  const content = meta?.getAttribute('content') || '';
  return content.split(',').map(o => o.trim()).filter(o => o.length > 0);
}

export function isUnrestricted(allowedOrigins) {
  return Array.isArray(allowedOrigins) && allowedOrigins.includes(ANY_ORIGIN);
}

function defaultPort(scheme) {
  if (scheme === 'http') return '80';
  if (scheme === 'https') return '443';
  return '';
}

/** True when origin matches an entry: exact, https://*.domain (subdomains only), host:* (any port), or a bare scheme. */
export function isAllowedOrigin(origin, allowedOrigins) {
  if (!origin || origin === 'null' || !Array.isArray(allowedOrigins)) return false;
  if (isUnrestricted(allowedOrigins)) return true;

  let url;
  try { url = new URL(origin); } catch (e) { return false; }
  const scheme = url.protocol.replace(/:$/, '').toLowerCase();
  const host = url.hostname.toLowerCase();
  const port = url.port || defaultPort(scheme);

  return allowedOrigins.some(entry => {
    const e = (entry || '').trim().toLowerCase();
    if (/^[a-z][a-z0-9+.-]*:$/.test(e)) return `${scheme}:` === e;

    const schemeEnd = e.indexOf('://');
    if (schemeEnd < 0 || e.substring(0, schemeEnd) !== scheme) return false;

    let entryHost = e.substring(schemeEnd + 3);
    let entryPort = '';
    const colon = entryHost.lastIndexOf(':');
    if (colon > 0) {
      entryPort = entryHost.substring(colon + 1);
      entryHost = entryHost.substring(0, colon);
    }
    if (entryPort !== '*' && (entryPort || defaultPort(scheme)) !== port) return false;

    if (entryHost.startsWith('*.')) return host.endsWith(entryHost.substring(1));
    return host === entryHost;
  });
}

/** True when source is win itself or a window above it. A frame FusionWeb embeds is never an ancestor. */
export function isAncestorWindow(source, win = window) {
  if (!source) return false;
  let w = win;
  for (let depth = 0; depth < 20; depth++) {
    if (source === w) return true;
    if (!w.parent || w.parent === w) return false;
    w = w.parent;
  }
  return false;
}

const warnedOrigins = new Set();
function warnOnce(kind, origin) {
  const key = `${kind}|${origin}`;
  if (warnedOrigins.has(key)) return;
  warnedOrigins.add(key);
  console.warn(`[embedOrigins] ${kind} refused for origin ${origin || '(unknown)'} — not in fusion-embed-origins`);
}

function topWindow(win) {
  let w = win;
  for (let depth = 0; depth < 20 && w.parent && w.parent !== w; depth++) w = w.parent;
  return w;
}

// The parent's and top window's origins as their own trusted messages reported them, per FusionWeb window.
const learnedHostOrigins = new WeakMap();

function learnHostOrigin(ev, win) {
  if (!ev.source || ev.source === win || !ev.origin || ev.origin === 'null') return;
  const learned = learnedHostOrigins.get(win) || {};
  if (ev.source === win.parent) learned.parent = ev.origin;
  if (ev.source === topWindow(win)) learned.top = ev.origin;
  learnedHostOrigins.set(win, learned);
}

/** Whether a 'message' event comes from a host FusionWeb trusts. */
export function isTrustedHostMessage(ev, allowedOrigins = readAllowedOrigins(), win = window) {
  if (!ev) return false;
  if (isUnrestricted(allowedOrigins)) {
    learnHostOrigin(ev, win);
    return true;
  }
  const selfOrigin = win.location.origin;
  if (!isAncestorWindow(ev.source, win)) {
    // A message with no source window from FusionWeb's own origin is FusionWeb itself.
    if (!(ev.source == null && ev.origin === selfOrigin)) return false;
  }
  if (ev.origin === selfOrigin || isAllowedOrigin(ev.origin, allowedOrigins)) {
    learnHostOrigin(ev, win);
    return true;
  }
  warnOnce('message', ev.origin);
  return false;
}

/** The origin of the parent ('parent') or the top window ('top'), or null when it cannot be known. */
export function resolveHostOrigin(target = 'parent', win = window) {
  if (!win.parent || win.parent === win) return win.location.origin;
  const ancestors = win.location.ancestorOrigins;
  if (ancestors && ancestors.length > 0)
    return target === 'top' ? ancestors[ancestors.length - 1] : ancestors[0];
  const learned = learnedHostOrigins.get(win)?.[target];
  if (learned) return learned;
  if (target === 'parent' && win.document?.referrer) {
    let referrerOrigin;
    try { referrerOrigin = new URL(win.document.referrer).origin; } catch (e) { return null; }
    // PR #1160 re-review: framed, a referrer from FusionWeb's own origin is FusionWeb's previous page
    // (the in-frame sign-in redirect), not the parent, so the parent's origin is unknown.
    if (referrerOrigin === win.location.origin) return null;
    return referrerOrigin;
  }
  return null;
}

/**
 * The targetOrigin for a message to the parent or top window, or null when it must not be sent (the
 * host's origin is unknown or not allowed). Unrestricted: the host's origin when known, else '*'.
 */
export function hostTargetOrigin(target = 'parent', allowedOrigins = readAllowedOrigins(), win = window) {
  const origin = resolveHostOrigin(target, win);
  if (isUnrestricted(allowedOrigins)) return origin || ANY_ORIGIN;
  if (origin && origin !== 'null' && (origin === win.location.origin || isAllowedOrigin(origin, allowedOrigins)))
    return origin;
  warnOnce('outbound message', origin);
  return null;
}
